import React, { useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, ExpandMore } from '@mui/icons-material';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Clickable month label that opens a year + 12-month grid, so any month is one click away.
// value / onChange use a JS Date set to the 1st of the month. Future months are disabled.
const MonthYearPicker = ({ value, onChange, minYear = 2000 }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewYear, setViewYear] = useState(value.getFullYear());

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const open = (event) => {
    setViewYear(value.getFullYear());
    setAnchorEl(event.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const select = (monthIndex) => {
    onChange(new Date(viewYear, monthIndex, 1));
    close();
  };

  const isFuture = (monthIndex) => viewYear > currentYear || (viewYear === currentYear && monthIndex > currentMonth);
  const isSelected = (monthIndex) => viewYear === value.getFullYear() && monthIndex === value.getMonth();
  const isCurrent = (monthIndex) => viewYear === currentYear && monthIndex === currentMonth;

  return (
    <>
      <Tooltip title="Choose month" describeChild>
        <Button
          onClick={open}
          endIcon={<ExpandMore />}
          aria-haspopup="dialog"
          sx={{
            bgcolor: '#3b82f6',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.9rem',
            textTransform: 'none',
            borderRadius: 999,
            px: 2.5,
            minWidth: 170,
            '&:hover': { bgcolor: '#2563eb' },
          }}
        >
          {value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Button>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { mt: 1, p: 2, borderRadius: 2, width: 280, boxShadow: '0 10px 30px rgba(15,23,42,0.15)' } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <IconButton size="small" onClick={() => setViewYear((y) => y - 1)} disabled={viewYear <= minYear} aria-label="Previous year">
            <ChevronLeft />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
            {viewYear}
          </Typography>
          <IconButton size="small" onClick={() => setViewYear((y) => y + 1)} disabled={viewYear >= currentYear} aria-label="Next year">
            <ChevronRight />
          </IconButton>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          {MONTHS.map((label, index) => {
            const selected = isSelected(index);
            const disabled = isFuture(index);
            return (
              <ButtonBase
                key={label}
                onClick={() => select(index)}
                disabled={disabled}
                aria-pressed={selected}
                sx={{
                  py: 1.25,
                  borderRadius: 1.5,
                  fontSize: '0.875rem',
                  fontWeight: selected ? 700 : 500,
                  color: disabled ? '#cbd5e1' : selected ? '#fff' : '#1e293b',
                  bgcolor: selected ? '#3b82f6' : 'transparent',
                  border: '1px solid',
                  borderColor: selected ? '#3b82f6' : isCurrent(index) ? '#93c5fd' : 'transparent',
                  '&:hover': { bgcolor: selected ? '#2563eb' : '#f1f5f9' },
                }}
              >
                {label}
              </ButtonBase>
            );
          })}
        </Box>

        <Button
          fullWidth
          size="small"
          onClick={() => {
            onChange(new Date(currentYear, currentMonth, 1));
            close();
          }}
          sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
        >
          Jump to current month
        </Button>
      </Popover>
    </>
  );
};

export default MonthYearPicker;
